var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Colors } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useEffect, useRef, useState } from 'react';
import { MEDIA_MIN_WIDTH } from '../../constants';
import { useTracking } from '../../tracking';
import { useDeepCompareEffect } from '../../utils/useDeepCompareEffect';
const styles = labelStyles('Video', {
    root: css({
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        maxHeight: '100%',
        margin: '0 auto',
        height: '100%',
    }),
    overlay: (hideOverlay) => css({
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
        backgroundColor: `rgba(0, 0, 0, 0.${hideOverlay ? 0 : 2})`,
        cursor: 'pointer',
        border: 'none',
    }),
    playIcon: css({
        width: '64px',
        height: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 253, 249, 0.8)',
        color: Colors.neutral700,
        borderRadius: '50%',
        transition: 'all 250ms',
        [MEDIA_MIN_WIDTH.small]: {
            width: '96px',
            height: '96px',
        },
        [MEDIA_MIN_WIDTH.small]: {
            width: '128px',
            height: '128px',
        },
        'button:hover &': {
            backgroundColor: 'rgba(255, 253, 249, 1)',
            transform: 'scale(0.95)',
            color: Colors.neutral800,
        },
        '& > svg': css({
            width: '50%',
            marginLeft: '7.5%',
        }),
    }),
    video: (fullHeight) => css(Object.assign({ display: 'block', maxWidth: '100%', maxHeight: '100%' }, (fullHeight && { height: '100%' }))),
});
export function Video(props) {
    const videoRef = useRef(null);
    const milestoneRef = useRef(0.25);
    const didScrollToPause = useRef(false);
    const [hasBeenStarted, setHasBeenStarted] = useState(false);
    const [textTracks, setTextTracks] = useState([]);
    const { emitUserInteractionEvent } = useTracking();
    useEffect(() => {
        if (!videoRef.current)
            return;
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const target = entry.target;
                if (!entry.isIntersecting && !target.paused) {
                    didScrollToPause.current = true;
                    target.pause();
                }
            }
        }, { threshold: 0.2 });
        if (props.scrollToPause) {
            observer.observe(videoRef.current);
        }
        return () => {
            observer.disconnect();
        };
    }, [props.scrollToPause]);
    useDeepCompareEffect(() => {
        if (props.textTracks) {
            Promise.all(props.textTracks.map((track) => __awaiter(this, void 0, void 0, function* () {
                const response = yield fetch(track.textTrackUrl);
                if (!response.ok) {
                    return track;
                }
                const blob = yield response.blob();
                return Object.assign(Object.assign({}, track), { textTrackUrl: URL.createObjectURL(blob) });
            }))).then((tracks) => {
                setTextTracks(tracks);
            });
        }
    }, [props.textTracks]);
    useEffect(() => {
        if (videoRef.current &&
            props.playbackRate &&
            props.playbackRate >= 0 &&
            props.playbackRate !== videoRef.current.playbackRate) {
            videoRef.current.playbackRate = props.playbackRate;
        }
    }, [props.playbackRate]);
    const play = () => {
        if (videoRef.current) {
            videoRef.current.play();
        }
    };
    const handlePlayEvent = (event) => {
        if (props.onPlay)
            props.onPlay();
        setHasBeenStarted(true);
        // Only emit a play event when triggered by a user, not by auto play
        if (!props.hideControls &&
            (!props.autoPlay || event.currentTarget.currentTime > 0)) {
            emitUserInteractionEvent({
                event_action: 'click',
                interaction_name: 'video',
                option_selected: 'play',
                custom_tracking_fields: {
                    current_time: event.currentTarget.currentTime.toString(),
                },
            });
        }
    };
    const handlePauseEvent = (event) => {
        // Only emit a pause event when triggered by a user, not by the video ending
        if (event.currentTarget.currentTime < event.currentTarget.duration) {
            emitUserInteractionEvent({
                event_action: didScrollToPause.current ? 'scroll' : 'click',
                interaction_name: 'video',
                option_selected: 'pause',
                custom_tracking_fields: {
                    current_time: event.currentTarget.currentTime.toString(),
                },
            });
        }
        didScrollToPause.current = false;
    };
    const handleSeekedEvent = (event) => {
        // Only emit a seek event when triggered by a user, not by the video looping
        if (!props.loop || (props.loop && event.currentTarget.currentTime > 0.01)) {
            emitUserInteractionEvent({
                event_action: 'click',
                interaction_name: 'video',
                option_selected: 'seek',
                custom_tracking_fields: {
                    current_time: event.currentTarget.currentTime.toString(),
                },
            });
        }
    };
    const handleTimeUpdateEvent = (event) => {
        const { currentTime, duration } = event.currentTarget;
        if (currentTime / duration >= milestoneRef.current) {
            emitUserInteractionEvent({
                event_action: 'view',
                interaction_name: 'video',
                option_selected: 'milestone',
                custom_tracking_fields: {
                    current_time: currentTime.toString(),
                    milestone: new Intl.NumberFormat('en-US', {
                        style: 'percent',
                    }).format(milestoneRef.current),
                },
            });
            milestoneRef.current += 0.25;
        }
    };
    return (_jsxs("div", Object.assign({ css: styles.root }, { children: [!props.autoPlay && !hasBeenStarted && (_jsx("button", Object.assign({ css: styles.overlay(props.hideOverlay), "data-testid": "video-overlay", onClick: play }, { children: _jsx("div", Object.assign({ css: styles.playIcon }, { children: _jsx("svg", Object.assign({ viewBox: "0 0 24 24", fill: "currentColor", xmlns: "http://www.w3.org/2000/svg" }, { children: _jsx("path", { d: "M19.5 12L5 3.63V20.37L19.5 12Z" }) })) })) }))), _jsx("video", Object.assign({ autoPlay: props.autoPlay, controls: hasBeenStarted && !props.hideControls, controlsList: "nodownload noplaybackrate", css: styles.video(props.fullHeight), "data-testid": "video", disablePictureInPicture: true, disableRemotePlayback: true, loop: props.loop, muted: props.autoPlay, onLoadedData: props.onLoadedData, onPlay: handlePlayEvent, playsInline: props.autoPlay, poster: props.posterImageUrl, preload: props.autoPlay ? 'auto' : 'metadata', ref: videoRef, src: props.videoUrl, title: props.title, onTimeUpdate: handleTimeUpdateEvent }, (!props.hideControls && {
                onSeeked: handleSeekedEvent,
                onPause: handlePauseEvent,
            }), { children: textTracks.map((track, index) => (_jsx("track", { default: track.default, kind: track.type, label: track.label, src: track.textTrackUrl, srcLang: track.language }, index))) }))] })));
}
//# sourceMappingURL=Video.js.map